import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getRecipeManagerData, duplicateRecipe, deleteCustomRecipe } from 'wasp/client/operations';
import { toast } from 'sonner';
import RecipeManagerPageView from './RecipeManagerPage.view';
import { ConfirmationDialog } from '../../components/ui/ConfirmationDialog';

/**
 * Custom debounce hook
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Recipe Manager Page Container
 * 
 * Centralizes all business logic, data fetching, and event handlers
 * for the professional recipe manager interface.
 */
export default function RecipeManagerPageContainer() {
  const navigate = useNavigate();
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [cuisineFilter, setCuisineFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [scalerDialogOpen, setScalerDialogOpen] = useState(false);
  const [recipeToScale, setRecipeToScale] = useState<any>(null);
  const [recipeToDelete, setRecipeToDelete] = useState<{ id: string; title: string } | null>(null);

  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Data fetching with performance optimizations
  const { 
    data, 
    isLoading, 
    error,
    refetch 
  } = useQuery(getRecipeManagerData, {
    search: debouncedSearch,
    cuisineFilter,
    difficultyFilter,
  }, {
    // Performance optimizations to prevent excessive refetching
    refetchOnWindowFocus: false,  // Don't refetch when window regains focus
    refetchOnReconnect: false,     // Don't refetch on network reconnect
    staleTime: 5 * 60 * 1000,      // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000,        // Keep unused data in cache for 10 minutes
  });

  // Actions
  const duplicateRecipeFn = useAction(duplicateRecipe);
  const deleteRecipeFn = useAction(deleteCustomRecipe);

  // Computed values
  const recipes = data?.recipes || [];
  const stats = data?.stats || {
    totalRecipes: 0,
    customRecipes: 0,
    scaledToday: 0,
    avgScaleFactor: 1.0,
  };

  // Get unique cuisines for filter
  const availableCuisines = useMemo(() => {
    const cuisines = recipes
      .map((r) => r.cuisine)
      .filter(Boolean) as string[];
    return Array.from(new Set(cuisines)).sort();
  }, [recipes]);

  // Event Handlers
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleCuisineChange = (cuisine: string) => {
    setCuisineFilter(cuisine);
  };

  const handleDifficultyChange = (difficulty: string) => {
    setDifficultyFilter(difficulty);
  };

  const handleViewModeChange = (mode: 'table' | 'grid') => {
    setViewMode(mode);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setCuisineFilter('');
    setDifficultyFilter('');
  };

  const handleSelectRecipe = (recipeId: string, selected: boolean) => {
    if (selected) {
      setSelectedRecipes([...selectedRecipes, recipeId]);
    } else {
      setSelectedRecipes(selectedRecipes.filter((id) => id !== recipeId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedRecipes(recipes.map((r) => r.id));
    } else {
      setSelectedRecipes([]);
    }
  };

  const handleScale = (recipe: any) => {
    setRecipeToScale(recipe);
    setScalerDialogOpen(true);
  };

  const handleCloseScaler = () => {
    setScalerDialogOpen(false);
    setRecipeToScale(null);
  };

  const handleRecipeSaved = () => {
    refetch(); // Refresh recipe list to show new scaled recipe
    toast.info('Recipe list updated');
  };

  const handleDuplicate = async (recipeId: string) => {
    try {
      const result: any = await duplicateRecipeFn({ recipeId });
      toast.success(result.message);
      refetch();
    } catch (error) {
      console.error('Failed to duplicate recipe:', error);
      toast.error('Failed to duplicate recipe');
    }
  };

  const handleDelete = (recipeId: string) => {
    const recipe = recipes.find((r) => r.id === recipeId);
    if (!recipe) return;

    setRecipeToDelete({ id: recipeId, title: recipe.title });
  };

  const confirmDelete = async () => {
    if (!recipeToDelete) return;

    try {
      const result: any = await deleteRecipeFn({ recipeId: recipeToDelete.id });
      toast.success(result.message);
      
      if (result.removedFromMealPlans > 0) {
        toast.info(`Removed from ${result.removedFromMealPlans} meal plans`);
      }
      
      // Remove from selected if it was selected
      setSelectedRecipes(selectedRecipes.filter((id) => id !== recipeToDelete.id));
      
      refetch();
    } catch (error) {
      console.error('Failed to delete recipe:', error);
      toast.error('Failed to delete recipe');
    } finally {
      setRecipeToDelete(null);
    }
  };

  const handleView = (recipeId: string) => {
    navigate(`/recipe/${recipeId}`);
  };

  const handleCreateRecipe = () => {
    navigate('/create-recipe');
  };

  // Pass everything to view
  return (
    <>
      <RecipeManagerPageView
        // Data
        recipes={recipes}
        stats={stats}
        isLoading={isLoading}
        error={error}
        
        // Filters
        searchQuery={searchQuery}
        cuisineFilter={cuisineFilter}
        difficultyFilter={difficultyFilter}
        viewMode={viewMode}
        availableCuisines={availableCuisines}
        
        // Selection
        selectedRecipes={selectedRecipes}
        
        // Scaler dialog
        scalerDialogOpen={scalerDialogOpen}
        recipeToScale={recipeToScale}
        onRecipeSaved={handleRecipeSaved}
        
        // Event handlers - Filters
        onSearchChange={handleSearchChange}
        onCuisineChange={handleCuisineChange}
        onDifficultyChange={handleDifficultyChange}
        onViewModeChange={handleViewModeChange}
        onClearFilters={handleClearFilters}
        
        // Event handlers - Selection
        onSelectRecipe={handleSelectRecipe}
        onSelectAll={handleSelectAll}
        
        // Event handlers - Recipe actions
        onScale={handleScale}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onView={handleView}
        onCreateRecipe={handleCreateRecipe}
        
        // Event handlers - Scaler dialog
        onCloseScaler={handleCloseScaler}
      />
      
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!recipeToDelete}
        onClose={() => setRecipeToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete Recipe?"
        description={
          recipeToDelete
            ? `Are you sure you want to delete "${recipeToDelete.title}"? This will permanently remove the recipe from your collection and all meal plans. This action cannot be undone.`
            : ''
        }
      />
    </>
  );
}
